import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import useDialog from 'hooks/useDialog';

import placeholder from 'lib/placeholder';
import { trackEvent } from 'lib/posthog';

import { useLocalWishlist } from 'providers/LocalWishlistProvider';

import { CLICK_COUPON_CTA } from 'constants/analytics';
import { WISHLIST_TABS } from 'constants/wishlist';

const WishlistDialog = dynamic(
  () => import('components/Wishlist/WishlistDialog'),
  { ssr: false }
);

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const WishlistCtaCard = ({ storeImage, storeName }) => {
  const { showDialog } = useDialog();
  const { addLocalCouponKeyword } = useLocalWishlist();

  const handleClick = async () => {
    trackEvent(CLICK_COUPON_CTA, {
      option: 'wishlist',
    });

    let preAdded = false;

    try {
      await addLocalCouponKeyword({
        keywordDisplay: storeName,
        keywordImage: storeImage,
      });
      preAdded = true;
    } catch {
      // 'already' — store already in local wishlist
      preAdded = true;
    }

    showDialog(WishlistDialog, {
      tabId: WISHLIST_TABS.COUPONS.id,
      sourceValue: 'coupon_cta',
      initialHasKeywordAdded: preAdded,
      onCloseWithKeywordAdded: () => {
        showDialog(LoginDialog, { sourceValue: 'coupon_cta' });
      },
    });
  };

  return (
    <Box className="grid min-h-36 grid-cols-[96px_1fr] gap-x-6 gap-y-2 rounded-4 border-2 border-primary-300 p-4 dark:border-primary-300 md:grid-cols-[auto_1fr_1fr_1fr_200px] md:gap-x-8 md:px-8 md:py-6">
      <div className="row-span-2 flex h-24 w-24 flex-col items-center justify-center self-center border-r border-dashed border-neutral-high-400 pr-6 dark:border-neutral-low-200 md:pr-8 xl:w-40">
        <Heading
          as="h3"
          className="cursor-pointer text-center text-neutral-low-400 dark:text-neutral-high-200 lg:text-[28px]"
          size="size2"
          onClick={handleClick}
        >
          ALERTAS
        </Heading>
        <Text
          className="cursor-pointer text-center text-neutral-low-400 dark:text-neutral-high-200"
          size="size1"
          onClick={handleClick}
        >
          Cupom
        </Text>
      </div>
      <div className="col-start-2 row-span-2 flex flex-col gap-2 self-center md:contents">
        <Heading
          as="h2"
          className="cursor-pointer text-sm font-semibold text-neutral-low-400 dark:text-neutral-high-200 md:col-span-3 md:col-start-2 md:row-span-2 md:self-center lg:text-xl"
          size="size4"
          onClick={handleClick}
        >
          {placeholder('descriptions.couponWishlistCta')} {storeName}
        </Heading>
        <Button
          buttonTextClassName="text-sm md:text-base"
          className="col-start-2 h-8 w-full self-center min-size-8 md:col-span-1 md:col-start-5 md:row-span-2 md:h-12 md:w-[200px] md:self-center md:justify-self-end md:min-size-10"
          size="size3"
          type="primary"
          full
          onClick={handleClick}
        >
          {placeholder('actions.couponWishlistCta')}
        </Button>
      </div>
    </Box>
  );
};

WishlistCtaCard.propTypes = {
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
};

export default WishlistCtaCard;
